/**
 * Data Grid Cell Resolver
 *
 * Maps Frappe field types to cell variant names.
 * Accepts string field types (not just ListViewColumnType) because
 * Frappe has types like Select, Small Text, Text Editor that aren't
 * in the ListViewColumnType union.
 */

import { READONLY_SYSTEM_FIELDS, type CellVariant } from './types';

/**
 * Resolves which cell variant to use for a given field.
 *
 * @param fieldType - The Frappe field type (e.g. "Data", "Currency", "Link")
 * @param fieldname - The field name
 * @param editableFields - Set of fieldnames that are editable
 * @returns The cell variant to render
 */
export function resolveCellVariant(
  fieldType: string | undefined,
  fieldname: string,
  editableFields: Set<string>,
): CellVariant {
  // System fields are never editable
  if (READONLY_SYSTEM_FIELDS.has(fieldname)) return 'readonly';

  // Fields not in the editable set are readonly
  if (!editableFields.has(fieldname)) return 'readonly';

  // Map Frappe field type to cell variant
  switch (fieldType) {
    case 'Data':
    case 'Small Text':
      return 'text';

    case 'Int':
    case 'Float':
    case 'Currency':
    case 'Percent':
      return 'number';

    case 'Link':
    case 'Select':
      return 'select';

    case 'Check':
      return 'checkbox';

    case 'Date':
    case 'Datetime':
      return 'date';

    case 'Text':
    case 'Text Editor':
    case 'Long Text':
      return 'longText';

    default:
      // Unknown field types stay read-only for safety
      return 'readonly';
  }
}
